import {
  AppVboWaardecheckFieldsFragment,
  WidgetPosition,
} from '~/graphql/types';

export type Props = {
  accountName: string;
};

export type AppVBOWaardecheckDefaultData = Omit<
  AppVboWaardecheckFieldsFragment,
  '__typename' | 'id' | 'accountId' | 'route'
>;

const defaultData = ({ accountName }: Props): AppVBOWaardecheckDefaultData => ({
  general: {
    __typename: 'AppVBOWaardecheckGeneral',
    background: {
      __typename: 'AppVBOWaardecheckContainer',
      background: '#75b8c8',
      color: '#aaaaaa',
    },
    primary: {
      __typename: 'AppVBOWaardecheckContainer',
      background: '#ee6055',
      color: '#ffffff',
    },
    secondary: {
      __typename: 'AppVBOWaardecheckContainer',
      background: '#2b4162',
      color: '#ffffff',
    },
    tertiary: {
      __typename: 'AppVBOWaardecheckContainer',
      background: '#ee6055',
      color: '#ffffff',
    },
    quaternary: {
      __typename: 'AppVBOWaardecheckContainer',
      background: '#2b4162',
      color: '#ffffff',
    },
    logoImage: null,
    logoLink: {
      __typename: 'AppVBOWaardecheckHref',
      href: { __typename: 'AppVBOWaardecheckUrl', nl: null, en: null },
    },
    fallbackImage: null,
    cookieStatement: {
      __typename: 'AppVBOWaardecheckHref',
      href: { __typename: 'AppVBOWaardecheckUrl', nl: null, en: null },
    },
    privacyStatement: {
      __typename: 'AppVBOWaardecheckHref',
      href: { __typename: 'AppVBOWaardecheckUrl', nl: null, en: null },
    },
    googleAnalytics: null,
    facebookPixel: null,
    currentLocationLabel: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Hier woon je nu',
      en: 'You live here now',
    },
    html: 'Hello world',
    showCookieBanner: true,
  },
  address: {
    __typename: 'AppVBOWaardecheckAddress',
    image: null,
    heading: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Wat is mijn huis waard?',
      en: 'What is the value of my property?',
    },
    subHeading: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Voer je adres in om de waardecheck te ontvangen',
      en: 'Enter your address to receive a property valuation',
    },
    primaryButton: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Volgende',
      en: 'Next',
    },
  },
  propertyType: {
    __typename: 'AppVBOWaardecheckPropertyType',
    image: null,
    heading: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Wat voor type woning is het?',
      en: 'What type of home is it?',
    },
  },
  why: {
    __typename: 'AppVBOWaardecheckWhy',
    image: null,
    heading: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Waarom doe je een waardecheck? Ik wil..',
      en: 'Why are you requesting this home valuation? I want..',
    },
  },
  when: {
    __typename: 'AppVBOWaardecheckWhen',
    heading: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Wanneer wil je verhuizen?',
      en: 'When would you like to move?',
    },
  },
  comparison: {
    __typename: 'AppVBOWaardecheckComparison',
    heading: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Is deze woning vergelijkbaar?',
      en: 'Is this a comparable home?',
    },
  },
  valuationFailure: {
    __typename: 'AppVBOWaardecheckValuationFailure',
    image: null,
    heading: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Sorry, we kunnen geen waardecheck opstellen..',
      en: 'Sorry, we cannot calculate a home valuation..',
    },
    body: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'We hebben te weinig vergelijkbare woningen voor een betrouwbare waardecheck.',
      en: 'We do not have enough comparable homes for a reliable home valuation.',
    },
    primaryButton: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Gratis waardebepaling aanvragen',
      en: 'Request a free home valuation',
    },
    secondaryButton: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Of, bekijk de vergelijkbare woningen nogmaals',
      en: 'Or, do the the comparability check again',
    },
  },
  valuationSuccess: {
    __typename: 'AppVBOWaardecheckValuationSuccess',
    image: null,
    heading: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Waar wil het waardecheck rapport ontvangen?',
      en: 'Where can we send the home valuation report?',
    },
    disclaimer: {
      __typename: 'AppVBOWaardecheckText',
      nl: `Door het versturen van dit formulier geef ik ${accountName} toestemming mij per e-mail het waarderapport toe te sturen en tot drie keer per e-mail en/of per telefoon contact met mij op te nemen met meer informatie over gerelateerde dienstverlening bij de koop of verkoop van mijn huis.`,
      en: `By submitting this form I allow ${accountName} to e-mail me the home valuation report and to contact me up to three times - either by phone or email -  regarding this home valuation or related broker services when buying or selling a home.`,
    },
    primaryButton: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Versturen',
      en: 'Submit',
    },
  },
  appraisalRequest: {
    __typename: 'AppVBOWaardecheckAppraisalRequest',
    image: null,
    heading: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Gratis waardebepaling aanvragen voor {{PROPERTY_ADDRESS}}',
      en: 'Request a free appraisal for {{PROPERTY_ADDRESS}}',
    },
    disclaimer: {
      __typename: 'AppVBOWaardecheckText',
      nl: `Door het versturen van dit formulier geef ik ${accountName} toestemming contact met mij op te nemen per telefoon of email voor het inplannen van een waardebepaling.`,
      en: `By submitting this form I allow ${accountName} to contact me either by phone or email with the purpose of scheduling an appointment for a home appraisal.`,
    },
    primaryButton: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Verstuur je aanvraag',
      en: 'Submit request',
    },
  },
  appraisalRequestSuccess: {
    __typename: 'AppVBOWaardecheckAppraisalRequestSuccess',
    image: null,
    heading: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Bedankt voor je aanvraag!',
      en: 'Thank you!',
    },
    body: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Wij nemen zo snel mogelijk contact met je op om de gratis waardebepaling in te plannen.',
      en: 'We will contact you as soon as possible to schedule a free home valuation.',
    },
    primaryButton: {
      __typename: 'AppVBOWaardecheckLink',
      text: {
        __typename: 'AppVBOWaardecheckText',
        nl: 'Sluit deze pagina',
        en: 'Close this page',
      },
      href: {
        __typename: 'AppVBOWaardecheckUrl',
        nl: 'https://dathuis.nl',
        en: null,
      },
    },
  },
  report: {
    __typename: 'AppVBOWaardecheckReport',
    image: null,
    heading: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'De waardecheck voor {{PROPERTY_ADDRESS}}',
      en: 'The home valuation for {{PROPERTY_ADDRESS}}',
    },
    estimationLabel: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Onze inschatting van jouw woning',
      en: 'Our estimation of your house',
    },
    estimationDisclaimer: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'De afgegeven waarde betreft een indicatiewaarde, berekend door Avix. Aan deze waardecheck kunnen geen rechten worden ontleend. Neem contact op met de vastgoedprofessional om een nauwkeuriger waardering voor deze woning te ontvangen.',
      en: 'The provided valuation is an indicative value, calculated by Avix. No rights can be derived from this home valuation report. Contact the real estate broker in order to receive a more accurate valuation for this home.',
    },
    conversionMessageHeading: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Geïndexeerde transactieprijs {{SALE_PRICE}}',
      en: 'Adjusted transaction price {{SALE_PRICE}}',
    },
    conversionMessageBodyLess: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Waarom is deze woning {{SALE_PRICE_DIFFERENCE}} minder waard dan jouw woning?',
      en: 'Why is this house worth {{SALE_PRICE_DIFFERENCE}} less than my value estimate?',
    },
    conversionMessageBodyMore: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Waarom is deze woning {{SALE_PRICE_DIFFERENCE}} meer waard dan jouw woning?',
      en: 'Why is this house worth {{SALE_PRICE_DIFFERENCE}} more than my value estimate?',
    },
    conversionMessageFooter: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Onze vastgoedprofessionals verklaren je graag het verschil',
      en: 'Our real estate professionals will gladly explain the difference',
    },
    conversionMessagePrimaryButton: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Bel mij terug',
      en: 'Call me back',
    },
    propertyPrimaryButton: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Verklaar verschil',
      en: 'Explain difference',
    },
    propertySalePrice: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'Geïndexeerde transactieprijs',
      en: 'Adjusted transaction price',
    },
    higherSalePrice: {
      __typename: 'AppVBOWaardecheckText',
      nl: '{{SALE_PRICE_DIFFERENCE}} meer dan de waarde van jouw woning',
      en: '{{SALE_PRICE_DIFFERENCE}} more than the value estimate of your property',
    },
    lowerSalePrice: {
      __typename: 'AppVBOWaardecheckText',
      nl: '{{SALE_PRICE_DIFFERENCE}} minder dan de waarde van jouw woning',
      en: '{{SALE_PRICE_DIFFERENCE}} less than the value estimate of your property',
    },
  },
  about: {
    __typename: 'AppVBOWaardecheckAbout',
    image: null,
    heading: {
      __typename: 'AppVBOWaardecheckText',
      nl: 'De klant staat altijd centraal',
      en: 'The customer is always number one',
    },
    body: {
      __typename: 'AppVBOWaardecheckText',
      nl: `${accountName} is een gespecialiseerd vastgoedprofessional. Dagelijks brengen wij als vastgoedprofessional, vraag- en aanbod samen in onze regio. In onze dienstverlening staat de tevredenheid van onze klanten centraal.`,
      en: `${accountName} is a highly specialised real estate professional. Our professionals are a driving force in matching supply and demand in our region. Customer satisfaction is central in everything we do.`,
    },
    primaryButton: {
      __typename: 'AppVBOWaardecheckLink',
      text: {
        __typename: 'AppVBOWaardecheckText',
        nl: 'Bezoek onze website',
        en: 'Visit our homepage',
      },
      href: {
        __typename: 'AppVBOWaardecheckUrl',
        nl: 'https://dathuis.nl',
        en: null,
      },
    },
  },
  widget: {
    __typename: 'AppVBOWaardecheckWidget',
    image: {
      __typename: 'AppVBOWaardecheckImage',
      url: 'https://dathuis-stash.imgix.net/dev_fallback_image.jpg?w=300&auto=format',
      s3key: '',
    },
    heading: {
      __typename: 'AppVBOWaardecheckText',
      en: 'What is my home worth?',
      nl: 'Wat is mijn huis waard?',
    },
    body: {
      __typename: 'AppVBOWaardecheckText',
      en: 'Easily calculate the estimated value of your house!',
      nl: 'Bereken eenvoudig de geschatte waarde van uw huis!',
    },
    button: {
      __typename: 'AppVBOWaardecheckText',
      en: 'Calculate now',
      nl: 'Bereken nu',
    },
    buttonColor: {
      __typename: 'AppVBOWaardecheckContainer',
      background: '#00A3C3',
      color: '#fff',
    },
    position: WidgetPosition.BottomRight,
    showImageOnMobile: true,
  },
});

export default defaultData;
